import Input from "../components/inputs/Input";
import { AiOutlineDashboard, AiOutlineLoading } from "react-icons/ai";
import {
  Alert,
  Badge,
  Button,
  Card,
  Pagination,
  Table,
  Tabs,
} from "flowbite-react";
import { useForm } from "react-hook-form";
import {
  ADMIN,
  API,
  AUDITS,
  CATEGORIES,
  PAYMENTS,
  USERS,
} from "../config/apiConfig";
import axios from "../config/axiosInstance";
import { useEffect, useMemo, useState } from "react";
import { useApplication } from "../contexts/ApplicationContext";
import { useQuery, useQueryClient } from "react-query";
import {
  HiAdjustments,
  HiOutlineAnnotation,
  HiUserCircle,
} from "react-icons/hi";
import { useNavigate, useParams } from "react-router-dom";
import DrawerLoading from "../components/DrawerLoading";
import {
  MdOutlineIntegrationInstructions,
  MdOutlinePayments,
  MdOutlineSupportAgent,
} from "react-icons/md";
import { FaMagnifyingGlass } from "react-icons/fa6";
import moment from "moment";
import { formatToReal } from "../utils/utils";
import { FaEdit, FaRegEdit, FaUserFriends } from "react-icons/fa";
import AdminUsersTableSpecific from "../components/tables/AdminUsersTableSpecific";
import AdminPaymentsTableSpecific from "../components/tables/AdminPaymentsTableSpecific";
import AdminHistoryTableSpecific from "../components/tables/AdminHistoryTableSpecific";
import { CopyBlock, dracula } from "react-code-blocks";
import AdminIntegrationSpecific from "../components/tables/AdminIntegrationSpecific";
import AdminAnnotationSpecific from "../components/tables/AdminAnnotationSpecific";
import TitleStripe from "../components/common/TitleStripe";
import Drawer from "../components/Drawer";
import CompanyEditForm from "../components/forms/CompanyEditForm";
import CurrencyInput from "../components/inputs/CurrencyInput";
import SelectInput from "../components/inputs/SelectInput";

const CompanyDetailsPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isDrawerEditCustomer, setIsDrawerEditCustomer] = useState(false);
  const { id } = useParams();
  const { user } = useApplication();
  const [currentPage, setCurrentPage] = useState(1);
  const [isSubmittingAddCredit, setIsSubmittingAddCredit] = useState(false);
  const [errorAddCredit, setErrorAddCredit] = useState(null);

  const {
    isLoading: isLoadingCompany,
    error: errorCompany,
    data: company,
  } = useQuery(["company"], async () => {
    if (id) {
      const { data } = await axios.get(
        `${ADMIN.GET_COMPANY_BY_INFO}/${id}?type=id`
      );
      return data.data;
    }
    return null;
  });

  const {
    isLoading: isLoadingCompanyQueries,
    error: errorCompanyQueries,
    data: companyQueries,
    refetch,
  } = useQuery(
    ["companyQueries", company],
    async () => {
      const { data: items } = await axios.get(
        `${ADMIN.GET_COMPANY_QUERIES}/${company?.id}`
      );
      return items.data;
    },
    {
      enabled: !!company?.id, // ✅ só roda quando company.id estiver definido
    }
  );

  const {
    isLoading: isLoadingOverview,
    error: errorOverview,
    data: overview,
  } = useQuery(
    ["overview", company?.id],
    async () => {
      const { data } = await axios.get(`${ADMIN.GET_OVERVIEW}/${company?.id}`);

      return data.data;
    },
    { enabled: !!company?.id }
  );

  const {
    isLoading: isLoadingCategories,
    error: errorCategories,
    data: categories,
  } = useQuery(["categories"], async () => {
    const { data } = await axios.get(
      `${CATEGORIES.GET_ALL}?itemsPerPage=${-1}`
    );

    return data.data.data;
  });

  const groupedQueries = useMemo(() => {
    return categories?.map((category) => ({
      ...category,
      queries:
        companyQueries?.filter((query) => query.categoryId === category.id) ||
        [],
    }));
  }, [categories, companyQueries]);

  const {
    isLoading: isLoadingInvoices,
    error: errorInvoices,
    data: invoices,
  } = useQuery(
    ["invoices", company?.id],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_PAYMENTS}?page=${1}&itemsPerPage=6&companyId=${
          company?.id
        }`
      );

      return data.data.data;
    },
    { enabled: !!company?.id }
  );

  const {
    register: registerAddCredit,
    handleSubmit: handleSubmitAddCredit,
    reset: resetAddCredit,
    control: controlCredit,
    formState: { errors: errorsAddCredit },
  } = useForm();

  const onSubmitAddCredit = async (data) => {
    try {
      setIsSubmittingAddCredit(true);

      if (+data?.value <= 0) {
        throw new Error("Valor inválido!");
      }

      let bodyData = {
        payment: {
          customer: company.id,
          value: data.value,
          methodPayment: data.methodPayment,
        },
      };

      await axios.post(`${PAYMENTS.ADD_CREDIT_MANUALLY}`, bodyData);

      queryClient.invalidateQueries(["company"]);
      queryClient.invalidateQueries(["invoices"]);
      queryClient.invalidateQueries(["overview"]);

      resetAddCredit();

      setErrorAddCredit(null);
    } catch (error) {
      setErrorAddCredit(error);
      console.error(error);
    } finally {
      setIsSubmittingAddCredit(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => navigate("/admin/customers")}
        size="xs"
        className="w-fit bg-gray-500 px-6 mb-2"
      >
        Voltar
      </Button>
      <div className="bg-white px-4 py-4 rounded-md shadow-md relative">
        <div className="flex gap-4 mb-5 flex flex-col">
          <div className="title-syncx text-lg">
            {company?.companyTypeId === "REPRESENTANTE"
              ? "Representante"
              : "Cliente"}
            : {company?.name || company?.socialReason}
          </div>
          <div className="-mt-2">
            <span className="font-semibold inline-block mr-1">
              Data de cadastro:
            </span>{" "}
            {moment(company?.createdAt)?.format("DD/MM/yyyy")}
          </div>
        </div>
        {/* <DrawerLoading /> */}
        <Tabs
          aria-label="Default tabs"
          id="tab-company-details"
          activeTab={activeTab}
          onActiveTabChange={(tabIndex) => setActiveTab(tabIndex)}
        >
          <Tabs.Item title="Visão Geral" icon={AiOutlineDashboard}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="rounded-md p-3 shadow-sm border border-gray-300 relative">
                {isLoadingCompany && <DrawerLoading />}
                <div className="font-semibold text-lg flex justify-between items-center">
                  <div>🧾 Identificação do Cliente</div>
                  <button
                    className="text-xs flex items-center"
                    onClick={() => setIsDrawerEditCustomer(true)}
                  >
                    <FaEdit className="inline-block mr-1" />
                    Editar
                  </button>
                </div>
                <div className="mt-4 flex gap-2 items-center">
                  <span className="font-semibold">Status:</span>
                  {company?.status === 1 ? (
                    <Badge color="green" className="w-fit">
                      Ativo
                    </Badge>
                  ) : company?.status === 0 ? (
                    <Badge color="failure" className="w-fit">
                      Bloqueado
                    </Badge>
                  ) : (
                    <Badge color="failure" className="w-fit">
                      Inadimplente
                    </Badge>
                  )}
                </div>
                <div className="mt-2">
                  <span className="inline-block mr-1 font-semibold">
                    Documento:
                  </span>
                  {company?.document || "Não informado"}
                </div>
                <div className="mt-2">
                  <span className="inline-block mr-1 font-semibold">
                    Telefone:
                  </span>
                  {company?.phoneNumber}
                </div>
                <div className="mt-2">
                  <span className="inline-block mr-1 font-semibold">
                    E-mail:
                  </span>
                  {company?.email}
                </div>
                <div className="mt-2">
                  <span className="inline-block mr-1 font-semibold">
                    Nome do responsável:
                  </span>
                  {company?.responsibleName || "Não informado"}
                </div>
                <div className="mt-2">
                  <span className="inline-block mr-1 font-semibold">
                    Doc. do responsável:
                  </span>
                  {company?.responsibleDocument || "Não informado"}
                </div>
                <div className="mt-2">
                  <span className="inline-block mr-1 font-semibold">
                    Plano:
                  </span>
                  {company?.typePayment === "PRE" ? "Pré-Pago" : "Pós-Pago"}
                </div>
              </div>
              <div className="rounded-md p-3 shadow-sm border border-gray-300 relative">
                {isLoadingOverview && <DrawerLoading />}
                <div className="font-semibold text-lg">
                  📊 Resumo Geral de Uso
                </div>
                <div className="flex flex-col gap-2 mt-4">
                  <div>
                    <span className="inline-block mr-1 font-semibold">
                      VCM:
                    </span>
                    {company?.vcm ? formatToReal(company?.vcm) : "Não definido"}
                  </div>
                  <div>
                    <span className="inline-block mr-1 font-semibold">
                      Valor total utilizado:
                    </span>
                    {formatToReal(overview?.invoice?.totalPrice || 0)}
                  </div>
                  <div className="flex flex-col">
                    <div className="underline font-semibold">Consultas</div>
                    <div className="flex gap-4 mt-2">
                      <div className="flex gap-1">
                        <div className="font-semibold">Qtd Total:</div>
                        <div>{overview?.query?.totalCount}</div>
                      </div>
                      <div className="flex gap-1">
                        <div className="font-semibold">Valor Total:</div>
                        <div>{formatToReal(overview?.query?.totalPrice)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-md p-3 shadow-sm border border-gray-300 relative">
                <form
                  className="mb-4 mt-2"
                  onSubmit={handleSubmitAddCredit(onSubmitAddCredit)}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Crédito atual:</span>{" "}
                    <Badge className="w-fit">
                      {formatToReal(company?.credit || 0)}
                    </Badge>{" "}
                  </div>

                  <CurrencyInput
                    label="Crédito"
                    name="value"
                    required={false}
                    register={registerAddCredit}
                    className="mt-4 mb-4"
                    control={controlCredit}
                  />
                  <div className="mb-4">
                    <SelectInput
                      register={registerAddCredit}
                      label="Método de pagamento"
                      name="methodPayment"
                    >
                      <option value="PIX">Pix</option>
                      <option value="BOLETO">Boleto</option>
                      <option value="CARTAO">Cartão de crédito</option>
                    </SelectInput>
                  </div>
                  <Button
                    type="submit"
                    className="w-full primary"
                    isProcessing={isSubmittingAddCredit}
                    processingSpinner={
                      <AiOutlineLoading className="h-6 w-6 animate-spin" />
                    }
                    disabled={
                      isSubmittingAddCredit || company?.typePayment === "POS"
                    }
                  >
                    Adicionar crédito
                  </Button>
                  <small className="text-center w-full block mt-2">
                    Não é possível adicionar saldo para clientes pós-pago.
                  </small>
                </form>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              <div className="rounded-md p-3 shadow-sm border border-gray-300 col-span-2 relative">
                {(isLoadingCompanyQueries || isLoadingCategories) && (
                  <DrawerLoading />
                )}
                <div className="title-syncx mb-4">
                  Tabela de consultas / preços
                </div>
                <div
                  className="overflow-y-auto pr-2"
                  style={{ maxHeight: "350px" }}
                >
                  {companyQueries?.length > 0 ? (
                    <div className="print-padding">
                      {groupedQueries?.map((cat, indexCat) => (
                        <div key={indexCat} className="mb-4">
                          <div className="font-semibold text-green-500 underline uppercase text-sm">
                            {cat.name}
                          </div>
                          {cat?.queries?.length > 0 ? (
                            <ul>
                              {cat?.queries?.map((query, index) => (
                                <li
                                  key={index}
                                  className="flex justify-between border-b border-gray-300 py-2"
                                >
                                  <div>{query?.name}</div>
                                  <div>{formatToReal(query?.price)}</div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <Alert className="mt-2" color="gray">
                              Nenhuma consulta {cat.name} contrada neste cliente
                            </Alert>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Alert className="mt-4" color="gray">
                      Nenhuma consulta disponível para este cliente.
                    </Alert>
                  )}
                </div>
              </div>
              <div className="rounded-md p-3 shadow-sm border border-gray-300 relative">
                {isLoadingInvoices && <DrawerLoading />}
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-lg">
                    🕓 Últimos pagamentos
                  </div>
                </div>
                {invoices?.length > 0 ? (
                  <ul>
                    {invoices?.map((payment, index) => (
                      <li
                        key={index}
                        className="pb-3 border-b border-gray-300 pt-3"
                      >
                        <div className="font-semibold">
                          Valor: {formatToReal(payment?.price)} |{" "}
                          {payment?.isPaid ? "Pago" : "Pendente"}
                        </div>
                        <div className="text-xs overflow-hidden text-ellipsis break-words">
                          Data:{" "}
                          {moment(payment?.createAt)?.format(
                            "DD/MM/yyyy | HH:MM"
                          )}
                          h | {payment?.type}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Alert className="mt-4" color="gray">
                    Nenhuma pagamento recente para este cliente.
                  </Alert>
                )}
              </div>
            </div>
          </Tabs.Item>
          <Tabs.Item title="Pagamentos" icon={MdOutlinePayments}>
            <AdminPaymentsTableSpecific
              companyId={company?.id}
              isActive={activeTab === 1}
            />
          </Tabs.Item>
          <Tabs.Item title="Consultas" icon={FaMagnifyingGlass}>
            <AdminHistoryTableSpecific
              companyId={company?.id}
              isActive={activeTab === 2}
            />
          </Tabs.Item>
          <Tabs.Item title="Usuários" icon={FaUserFriends}>
            <AdminUsersTableSpecific
              companyId={company?.id}
              isActive={activeTab === 3}
            />
          </Tabs.Item>
          <Tabs.Item title="Integração" icon={MdOutlineIntegrationInstructions}>
            <AdminIntegrationSpecific
              companyId={company?.id}
              isActive={activeTab === 4}
            />
          </Tabs.Item>
          <Tabs.Item title="Anotações" icon={HiOutlineAnnotation}>
            <AdminAnnotationSpecific
              companyId={company?.id}
              isActive={activeTab === 5}
            />
          </Tabs.Item>
        </Tabs>
        <Drawer
          title={`Editar cliente`}
          onClose={setIsDrawerEditCustomer}
          isOpen={isDrawerEditCustomer}
          className={"w-full md:w-4/6 lg:w-3/6"}
        >
          <CompanyEditForm
            company={company}
            onClose={setIsDrawerEditCustomer}
            categories={categories}
            fetchCompanies={() => queryClient.invalidateQueries(["company"])}
          />
        </Drawer>
      </div>
    </>
  );
};

export default CompanyDetailsPage;
